import { useMemo } from "react";
import { ProjectWithChildren } from "../bitmaker/schemas/Projects/ProjectWithChildren";

type ProjectWithMeta = ProjectWithChildren & {
  isMatch?: boolean;
  hasMatchingDescendant?: boolean;
};

export function useFilteredProjects(
  projects: ProjectWithChildren[],
  filters: any,
) {
  const filteredProjects = useMemo(() => {
    const matches = (item: ProjectWithChildren) => {
      const accountMatch =
        !filters.account?.length ||
        (item.account != null &&
          filters.account.includes(item.account));

      const assigneeMatch =
        !filters.assignee?.length ||
        (item.assignee_id != null &&
          filters.assignee.includes(Number(item.assignee_id)));

      const tagsMatch =
        !filters.tags?.length ||
        (item.tags ?? []).some((t) => filters.tags.includes(t.name));

      return accountMatch && assigneeMatch && tagsMatch;
    };

    const recursive = (list: ProjectWithChildren[]): ProjectWithMeta[] => {
      return list
        .map((item) => {
          const children = item.children ? recursive(item.children) : [];

          const selfMatch = matches(item);

          if (selfMatch || children.length > 0) {
            return {
              ...item,
              isMatch: selfMatch,
              hasMatchingDescendant: children.length > 0,
              children: children.length ? children : undefined,
            };
          }

          return null;
        })
        .filter(Boolean) as ProjectWithMeta[];
    };

    return recursive(projects ?? []);
  }, [projects, filters]);

  const expandedRowKeys = useMemo(() => {
    const keys: number[] = [];

    const traverse = (list: ProjectWithMeta[]) => {
      list.forEach((item) => {
        if (item.children) {
          keys.push(item.id);
          traverse(item.children);
        }
      });
    };

    traverse(filteredProjects);
    return keys;
  }, [filteredProjects]);

  return { filteredProjects, expandedRowKeys };
}
